import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { voucherService, VoucherBalance } from '../services/voucherService';
import { useWallet } from './WalletProvider';

// Types
interface VoucherContextType {
  // State
  voucherBalance: VoucherBalance;
  isLoadingVoucherBalance: boolean;
  
  // Computed values
  hasVoucherCredits: boolean;
  totalVoucherCredits: number;
  voucherCount: number;
  
  // Actions
  refreshVoucherBalance: () => Promise<void>;
  canAffordVoucherTrade: (amount: number) => boolean;
  executeVoucherBuyTrade: (tokenMint: string, solAmount: number, exchanges: any[]) => Promise<any>;
  executeVoucherSellTrade: (tokenMint: string, exchanges: any[]) => Promise<any>;
  claimPromoCode: (code: string) => Promise<any>;
  hasVoucherTokenPosition: (tokenMint: string) => Promise<{ hasPosition: boolean; tokenBalance: number }>;
  
  // NEW: Round integration methods
  executeVoucherBuyTradeWithRoundIntegration: (tradeData: {
    userId: string;
    tokenMint: string;
    solAmount: number;
    exchanges: any[];
    roundId: string;
    userWalletAddress: string;
  }) => Promise<any>;
  executeVoucherSellTradeWithRoundIntegration: (tradeData: {
    userId: string;
    tokenMint: string;
    exchanges: any[];
    roundId: string;
    userWalletAddress: string;
  }) => Promise<any>;
  
  // Transaction tracking
  onVoucherTransactionComplete: (type: 'buy' | 'sell', amount: number) => void;
}

// Create context
const VoucherContext = createContext<VoucherContextType | undefined>(undefined);

// Provider component
export const VoucherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, address } = useWallet();
  
  // State
  const [voucherBalance, setVoucherBalance] = useState<VoucherBalance>({
    hasVoucherCredits: false,
    totalCredits: 0,
    voucherCount: 0
  });
  const [isLoadingVoucherBalance, setIsLoadingVoucherBalance] = useState(false);
  
  // Ref to prevent multiple simultaneous refresh calls
  const refreshInProgress = useRef(false);

  // Get user info helper (now just returns wallet address)
  const getUserInfo = useCallback(() => {
    if (!isAuthenticated || !address) return null;
    
    return {
      walletAddress: address
    };
  }, [isAuthenticated, address]);

  // Clear voucher state when user logs out
  useEffect(() => {
    if (!isAuthenticated || !address) {
      setVoucherBalance({
        hasVoucherCredits: false,
        totalCredits: 0,
        voucherCount: 0
      });
      setIsLoadingVoucherBalance(false);
      refreshInProgress.current = false;
    }
  }, [isAuthenticated, address]);

  // Refresh voucher balance with deduplication
  const refreshVoucherBalance = useCallback(async () => {
    // Prevent multiple simultaneous refresh calls
    if (refreshInProgress.current) {
      return;
    }

    const userInfo = getUserInfo();
    if (!userInfo) {
      setVoucherBalance({
        hasVoucherCredits: false,
        totalCredits: 0,
        voucherCount: 0
      });
      return;
    }

    refreshInProgress.current = true;
    setIsLoadingVoucherBalance(true);
    
    try {
      const balance = await voucherService.getVoucherBalance(userInfo.walletAddress);
      setVoucherBalance(balance);
    } catch (error) {
      console.error('Failed to refresh voucher balance:', error);
      setVoucherBalance({
        hasVoucherCredits: false,
        totalCredits: 0,
        voucherCount: 0
      });
    } finally {
      setIsLoadingVoucherBalance(false);
      refreshInProgress.current = false;
    }
  }, [getUserInfo]);

  // Load voucher balance when wallet is connected
  useEffect(() => {
    if (isAuthenticated && address) {
      refreshVoucherBalance();
    } else {
      setVoucherBalance({
        hasVoucherCredits: false,
        totalCredits: 0,
        voucherCount: 0
      });
    }
  }, [isAuthenticated, address, refreshVoucherBalance]);

  // No longer need Twitter auth storage listener

  // Computed values
  const hasVoucherCredits = voucherBalance.hasVoucherCredits;
  const totalVoucherCredits = voucherBalance.totalCredits;
  const voucherCount = voucherBalance.voucherCount;

  // Check if user can afford a voucher trade
  const canAffordVoucherTrade = useCallback((solAmount: number) => {
    return hasVoucherCredits && totalVoucherCredits >= solAmount;
  }, [hasVoucherCredits, totalVoucherCredits]);

  // Execute voucher buy trade
  const executeVoucherBuyTrade = useCallback(async (
    tokenMint: string,
    solAmount: number,
    exchanges: any[]
  ) => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return { success: false, error: 'User not authenticated' };
    }

    // Note: The old voucher trade functions are designed for token trading with Twitter auth
    // For betting, we don't need these functions - we use the new wallet-based system
    return { 
      success: false, 
      error: 'Voucher token trading not supported with wallet-only auth'
    };
  }, [getUserInfo]);

  // Execute voucher sell trade
  const executeVoucherSellTrade = useCallback(async (
    tokenMint: string,
    exchanges: any[]
  ) => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return { success: false, error: 'User not authenticated' };
    }

    // Note: The old voucher trade functions are designed for token trading with Twitter auth
    // For betting, we don't need these functions - we use the new wallet-based system
    return { 
      success: false, 
      error: 'Voucher token trading not supported with wallet-only auth'
    };
  }, [getUserInfo]);

  // Claim promo code
  const claimPromoCode = useCallback(async (promoCode: string) => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const result = await voucherService.claimPromoCode(
        userInfo.walletAddress,
        promoCode
      );

      // Always refresh balance after claim attempt
      await refreshVoucherBalance();

      return result;
    } catch (error) {
      console.error('Failed to claim promo code:', error);
      // Still refresh balance in case of partial success
      await refreshVoucherBalance();
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to claim promo code' 
      };
    }
  }, [getUserInfo]);

  // Check if user has voucher-bought token position
  const hasVoucherTokenPosition = useCallback(async (tokenMint: string) => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return { hasPosition: false, tokenBalance: 0 };
    }

    // Note: Token position checking not supported with wallet-only auth
    // For betting, we don't need token positions
    return { hasPosition: false, tokenBalance: 0 };
  }, [getUserInfo]);

  // NEW: Execute voucher buy trade with round integration
  const executeVoucherBuyTradeWithRoundIntegration = useCallback(async (tradeData: {
    userId: string;
    tokenMint: string;
    solAmount: number;
    exchanges: any[];
    roundId: string;
    userWalletAddress: string;
  }) => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return { success: false, error: 'User not authenticated' };
    }

    // Note: Round integration trading not supported with wallet-only auth
    // For betting, we use the new voucher betting endpoint instead
    return { 
      success: false, 
      error: 'Voucher round trading not supported with wallet-only auth'
    };
  }, [getUserInfo]);

  // NEW: Execute voucher sell trade with round integration
  const executeVoucherSellTradeWithRoundIntegration = useCallback(async (tradeData: {
    userId: string;
    tokenMint: string;
    exchanges: any[];
    roundId: string;
    userWalletAddress: string;
  }) => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return { success: false, error: 'User not authenticated' };
    }

    // Note: Round integration trading not supported with wallet-only auth  
    // For betting, we use the new voucher betting endpoint instead
    return { 
      success: false, 
      error: 'Voucher round trading not supported with wallet-only auth'
    };
  }, [getUserInfo]);

  // Transaction completion callback for additional tracking
  const onVoucherTransactionComplete = useCallback((type: 'buy' | 'sell', amount: number) => {
    console.log('🎫 Voucher transaction completed:', { type, amount });
    // Additional tracking or side effects can be added here
    // The balance refresh is already handled in the execute functions
  }, []);

  // Context value
  const contextValue: VoucherContextType = {
    // State
    voucherBalance,
    isLoadingVoucherBalance,
    
    // Computed values
    hasVoucherCredits,
    totalVoucherCredits,
    voucherCount,
    
    // Actions
    refreshVoucherBalance,
    canAffordVoucherTrade,
    executeVoucherBuyTrade,
    executeVoucherSellTrade,
    claimPromoCode,
    hasVoucherTokenPosition,
    
    // NEW: Round integration methods
    executeVoucherBuyTradeWithRoundIntegration,
    executeVoucherSellTradeWithRoundIntegration,
    
    // Transaction tracking
    onVoucherTransactionComplete
  };

  return (
    <VoucherContext.Provider value={contextValue}>
      {children}
    </VoucherContext.Provider>
  );
};

// Custom hook to use the voucher context
export const useVoucherContext = () => {
  const context = useContext(VoucherContext);
  if (context === undefined) {
    throw new Error('useVoucherContext must be used within a VoucherProvider');
  }
  return context;
};

// Export the context for testing purposes
export { VoucherContext }; 